import { useState, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { humanizePhoneAuthError } from '@/lib/phone-auth-error';
import { composeE164FromLocal } from '@/lib/phone-utils';
import { leaveForTelemetryDeniedLogin } from '@/lib/privacy/auth-document';
import { AccessRequestForm } from './AccessRequestForm';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { ChevronDown } from 'lucide-react';

interface PhoneCaptureProps {
  defaultChannel?: 'sms' | 'whatsapp';
  onVerified?: (phoneNumber: string) => void;
  className?: string;
  userId?: string; // link phone to current user when available
  mode?: 'login' | 'register';
}

const COUNTRIES = (() => {
  try {
    const codes = (getCountries as any)() as string[];
    const enriched = codes.map((iso2) => {
      const dial = (getCountryCallingCode as any)(iso2) as string;
      const label = new Intl.DisplayNames(['en'], { type: 'region' }).of(iso2) || iso2.toUpperCase();
      const flag = iso2.replace(/./g, (ch) => String.fromCodePoint(127397 + ch.toUpperCase().charCodeAt(0)));
      return { code: dial, label, flag } as any;
    });
    const us = enriched.find((c) => c.label === 'United States');
    const rest = enriched.filter((c) => c.label !== 'United States').sort((a, b) => a.label.localeCompare(b.label));
    return us ? [us, ...rest] : rest;
  } catch {
    return [
      { code: '1', label: 'United States', flag: '🇺🇸' },
      { code: '44', label: 'United Kingdom', flag: '🇬🇧' },
      { code: '971', label: 'United Arab Emirates', flag: '🇦🇪' },
      { code: '65', label: 'Singapore', flag: '🇸🇬' },
      { code: '91', label: 'India', flag: '🇮🇳' },
    ] as any;
  }
})();

function normalizeDigits(input: string): string {
  return (input || '').replace(/\D+/g, '');
}

export function PhoneCapture({ defaultChannel = 'sms', onVerified, className, userId, mode = 'login' }: PhoneCaptureProps) {
  const [countryCode, setCountryCode] = useState('1');
  const [localNumber, setLocalNumber] = useState('');
  const [channel, setChannel] = useState<'sms' | 'whatsapp'>(defaultChannel);
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'enter' | 'verify' | 'name' | 'access-request'>('enter');
  const [error, setError] = useState<string | null>(null);
  const [fullName, setFullName] = useState('');
  const [instagramHandle, setInstagramHandle] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [verifiedUserId, _setVerifiedUserId] = useState<string | null>(null);
  const sendLock = useRef(false);
  const verifyLock = useRef(false);

  const e164 = useMemo(() => {
    return composeE164FromLocal(countryCode, localNumber) || '';
  }, [countryCode, localNumber]);

  const countryOptions = useMemo(() => {
    return COUNTRIES;
  }, []);

  // Same match the native <select> makes for a shared dial code: the first option wins.
  const selectedCountry = useMemo(
    () => (countryOptions as Array<{ code: string; flag: string }>).find((c) => c.code === countryCode),
    [countryOptions, countryCode],
  );

  const isValidLocal = useMemo(() => {
    const digits = normalizeDigits(localNumber);
    const len = digits.length;
    return len >= 5 && len <= 15;
  }, [localNumber]);

  const authTab = mode === 'register' ? 'sign-up' : 'sign-in';

  const start = async () => {
    if (leaveForTelemetryDeniedLogin(authTab)) return;
    setError(null);
    if (!isValidLocal || !e164) {
      setError('Enter a valid phone number');
      return;
    }
    if (mode === 'register') {
      const email = (signupEmail || '').trim();
      const emailOk = /[^@\s]+@[^@\s]+\.[^@\s]+/.test(email);
      if (!emailOk) {
        setError('Enter a valid email');
        return;
      }
      if (!fullName || fullName.trim().length < 2) {
        setError('Enter your full name');
        return;
      }
    }
    if (sendLock.current) return;
    sendLock.current = true;
    verifyLock.current = false;
    setSending(true);
    try {
      console.log('[PhoneCapture] Sending OTP to:', e164, 'via channel:', channel);

      // Route through server to enable robust fallbacks in production
      const sendRes = await fetch('/.netlify/functions/phone-auth-native', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send-otp',
          phone: e164,
          channel,
          user_id: userId,
          mode,
          full_name: mode === 'register' ? fullName : undefined,
          instagram_handle: mode === 'register' ? instagramHandle : undefined,
          email: mode === 'register' && signupEmail ? signupEmail : undefined
        })
      });

      if (!sendRes.ok) {
        const err = await sendRes.text();
        throw new Error(humanizePhoneAuthError(err, 'Failed to send code'));
      }

      console.log('[PhoneCapture] OTP send initiated via server');
      setStep('verify');
    } catch (e: any) {
      sendLock.current = false;
      console.error('[PhoneCapture] Error sending OTP:', e);
      setError(e.message || 'Failed to send code');
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    if (leaveForTelemetryDeniedLogin(authTab)) return;
    setError(null);
    if (!code || code.length < 4) {
      setError('Enter the 6-digit code');
      return;
    }
    if (verifyLock.current) return;
    verifyLock.current = true;
    setSending(true);
    try {
      console.log('[PhoneCapture] Verifying OTP for:', e164, 'with code:', code);
      const cleanCode = code.replace(/\D/g, '');

      // Use server-side verification for consistency and cleanup
      const verifyRes = await fetch('/.netlify/functions/phone-auth-native', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify-otp', phone: e164, code: cleanCode, channel })
      });

      if (!verifyRes.ok) {
        const err = await verifyRes.json().catch(() => ({} as any));
        throw new Error(humanizePhoneAuthError(err, 'Invalid or expired code'));
      }

      const verifyData = await verifyRes.json();
      console.log('[PhoneCapture] Verify response received');

      const _refreshSessionIfNeeded = async (requiresReauth?: boolean): Promise<boolean> => {
        if (!requiresReauth) return false;

        console.log('[PhoneCapture] Refreshing session after phone linking');
        const { data: refreshData } = await supabase.auth.refreshSession();

        if (refreshData?.session) {
          console.log('[PhoneCapture] Session refreshed successfully');
          return true;
        }

        console.warn('[PhoneCapture] Session refresh failed');
        return false;
      };

      if (verifyData.whatsappLogin && verifyData.hashed_token) {
        console.log('[PhoneCapture] WhatsApp login - verifying hashed token');

        const { data: sessionData, error: sessionError } = await supabase.auth.verifyOtp({
          token_hash: verifyData.hashed_token,
          type: 'magiclink'
        });

        if (sessionError || !sessionData?.session) {
          verifyLock.current = false;
          console.error('[PhoneCapture] Failed to create session from hashed token:', sessionError);
          setError('Login verified but failed to create session. Please try again.');
          return;
        }

        console.log('[PhoneCapture] Session created successfully from hashed token');
        verifyData.session = sessionData.session;
      }

      // If server returned a session, activate it in the client immediately.
      // Never do that in a document that already loaded analytics.
      try {
        if (verifyData?.session?.access_token && verifyData?.session?.refresh_token) {
          if (leaveForTelemetryDeniedLogin(authTab)) return;
          await (supabase as any).auth.setSession({
            access_token: verifyData.session.access_token,
            refresh_token: verifyData.session.refresh_token
          });
        }
      } catch {}

      onVerified?.(e164);
    } catch (e: any) {
      verifyLock.current = false;
      console.error('[PhoneCapture] Error:', e);
      setError(e.message || 'Invalid or expired code');
    } finally {
      setSending(false);
    }
  };

  const saveName = async () => {
    setError(null);
    if (!fullName || fullName.trim().length < 2) {
      setError('Please enter your name');
      return;
    }
    setSending(true);
    try {
      // Name is already saved in members table via phone-auth-native
      onVerified?.(e164);
    } catch (e: any) {
      setError(e.message || 'Failed to save name');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`rounded-xl border border-sand-new bg-bone p-4 ${className || ''}`}>
      <div className="text-charcoal font-brand-display text-lg mb-2">Verify your number</div>
      <p className="text-ash text-sm mb-3">We’ll send a one‑time code to sign you in and personalize your experience.</p>

      {step === 'enter' && (
        <div className="space-y-3">
          <div className="flex items-stretch gap-2">
            {/* Compact picker: the closed box shows only flag + dial code so the number
                field keeps most of the row on a 320-375px phone; the native list still
                shows full country names. The real <select> sits invisibly on top. */}
            <div
              data-testid="phone-country-picker"
              className="relative shrink-0 rounded-lg border border-sand-new bg-bone text-charcoal focus-within:ring-2 focus-within:ring-olive"
            >
              <span aria-hidden="true" className="pointer-events-none flex h-full items-center gap-1 pl-3 pr-2 py-2 whitespace-nowrap">
                {selectedCountry?.flag} +{countryCode}
                <ChevronDown className="h-4 w-4 text-ash" />
              </span>
              <select
                aria-label="Country code"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
              >
                {countryOptions.map((c: { code: string; label: string; flag: string }, idx: number) => (
                  <option key={`${c.code}-${idx}`} value={c.code}>
                    {c.flag} +{c.code} {c.label}
                  </option>
                ))}
              </select>
            </div>
            <input
              inputMode="numeric"
              placeholder={countryCode === '1' ? '(555) 123-4567' : 'Phone number'}
              value={localNumber}
              onChange={(e) => {
                const filtered = e.target.value.replace(/[^\d\s\-()]/g, '');
                setLocalNumber(filtered);
              }}
              className="flex-1 min-w-0 w-full px-3 py-2 rounded-lg border border-sand-new bg-bone text-charcoal"
            />
          </div>
          {mode === 'register' && (
            <div className="grid grid-cols-1 gap-2">
              <input
                placeholder="Full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="px-3 py-2 rounded-lg border border-sand-new bg-bone text-charcoal w-full"
              />
              <input
                type="email"
                placeholder="Email (required)"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                className="px-3 py-2 rounded-lg border border-sand-new bg-bone text-charcoal w-full"
              />
              <input
                placeholder="Instagram handle (optional)"
                value={instagramHandle}
                onChange={(e) => setInstagramHandle(e.target.value)}
                className="px-3 py-2 rounded-lg border border-sand-new bg-bone text-charcoal w-full"
              />
            </div>
          )}
          <div className="flex gap-3 text-xs text-ash items-center">
            <label className="inline-flex items-center gap-1">
              <input type="radio" name="ch" checked={channel === 'whatsapp'} onChange={() => setChannel('whatsapp')} /> WhatsApp
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="radio" name="ch" checked={channel === 'sms'} onChange={() => setChannel('sms')} /> SMS
            </label>
            {e164 && <span className="ml-auto max-w-[40%] truncate">{e164}</span>}
          </div>
          {error && <div className="text-rust text-sm">{error}</div>}
          {!error && localNumber && !isValidLocal && (
            <div className="text-amber-500 text-xs">
              Please enter a valid phone number
            </div>
          )}
          <button
            disabled={sending}
            onClick={start}
            className="px-4 py-2 rounded-lg bg-olive text-bone hover:bg-olive/90 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send code'}
          </button>
          <p className="text-[11px] text-ash/80">Used for sign‑in and share updates. Opt out any time. SMS & data rates may apply.</p>
        </div>
      )}

      {step === 'verify' && (
        <div className="space-y-3">
          <div className="text-sm text-charcoal">Enter the 6-digit code we sent to {e164}</div>
          <input
            inputMode="numeric"
            autoComplete="one-time-code"
            name="one-time-code"
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D+/g, ''))}
            className="px-3 py-2 rounded-lg border border-sand-new bg-bone text-charcoal w-40 tracking-widest"
          />
          {error && <div className="text-rust text-sm">{error}</div>}
          <div className="flex gap-2">
            <button
              disabled={sending}
              onClick={verify}
              className="px-4 py-2 rounded-lg bg-olive text-bone hover:bg-olive/90 disabled:opacity-50"
            >
              {sending ? 'Verifying…' : 'Verify'}
            </button>
            <button
              disabled={sending}
              onClick={() => {
                sendLock.current = false;
                verifyLock.current = false;
                setStep('enter');
              }}
              className="px-3 py-2 rounded-lg bg-parchment border border-sand-new text-charcoal"
            >
              Edit number
            </button>
          </div>
        </div>
      )}

      {step === 'name' && (
        <div className="space-y-3">
          <div className="text-sm text-charcoal">Welcome! What should we call you?</div>
          <input
            placeholder="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoCapitalize="words"
            className="px-3 py-2 rounded-lg border border-sand-new bg-bone text-charcoal w-full"
          />
          {error && <div className="text-rust text-sm">{error}</div>}
          <div className="flex gap-2">
            <button
              disabled={sending}
              onClick={saveName}
              className="px-4 py-2 rounded-lg bg-olive text-bone hover:bg-olive/90 disabled:opacity-50"
            >
              {sending ? 'Saving…' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {step === 'access-request' && verifiedUserId && (
        <AccessRequestForm
          userId={verifiedUserId}
          phoneNumber={e164}
          onComplete={() => onVerified?.(e164)}
        />
      )}
    </div>
  );
}
